import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { UnknownTag } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputNullableInt,
    ValidatedInt,
    InputText,
    ValidatedText,
    InputSelect,
    DataSourceLookup,
    InputDateTime,
    DateComp,
} from '@/components';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class UnknownTagGuiDef extends GuiDef<UnknownTag> {
    constructor() {
        super('UnknownTag');

        const EventCommon = storeToRefs(useEventCommonStore());
        const StationsSource = computed(() => {
            return EventCommon.StationsAsSelectLine.value;
        });

        const Tag = new FieldDef<UnknownTag>();
        Tag.Name = 'Tag';
        Tag.FieldName = 'Tag';
        Tag.DefinedFor = 'Both';
        Tag.TranslationKey = 'unknowntag.tag';
        Tag.Editable = false;
        Tag.Filterable = false;
        Tag.Sortable = true;
        Tag.CssClasses = '';
        Tag.value = (unknowntag) => unknowntag.Tag;
        Tag.setValue = () => {};
        Tag.DetailEditor = markRaw(InputNullableInt);
        Tag.DetailEditorProps = {};
        Tag.CellEditor = markRaw(InputNullableInt);
        Tag.CellEditorProps = {};
        Tag.CellRenderer = markRaw(ValidatedInt);
        Tag.CellRendererProps = {};
        Tag.Validator = null;
        Tag.SortDirection = undefined;
        Tag.cellClassRules = undefined;
        Tag.MinWidth = undefined;
        Tag.MaxWidth = undefined;
        Tag.Tooltip = null;
        this.Fields.push(Tag);

        const AlphaTag = new FieldDef<UnknownTag>();
        AlphaTag.Name = 'AlphaTag';
        AlphaTag.FieldName = 'AlphaTag';
        AlphaTag.DefinedFor = 'Both';
        AlphaTag.TranslationKey = 'unknowntag.alphatag';
        AlphaTag.Editable = false;
        AlphaTag.Filterable = true;
        AlphaTag.Sortable = true;
        AlphaTag.CssClasses = '';
        AlphaTag.value = (unknowntag) => unknowntag.AlphaTag;
        AlphaTag.setValue = () => {};
        AlphaTag.DetailEditor = markRaw(InputText);
        AlphaTag.DetailEditorProps = {};
        AlphaTag.CellEditor = markRaw(InputText);
        AlphaTag.CellEditorProps = {};
        AlphaTag.CellRenderer = markRaw(ValidatedText);
        AlphaTag.CellRendererProps = {};
        AlphaTag.Validator = null;
        AlphaTag.SortDirection = undefined;
        AlphaTag.cellClassRules = undefined;
        AlphaTag.MinWidth = undefined;
        AlphaTag.MaxWidth = undefined;
        AlphaTag.Tooltip = null;
        this.Fields.push(AlphaTag);

        const StationId = new FieldDef<UnknownTag>();
        StationId.Name = 'StationId';
        StationId.FieldName = 'StationId';
        StationId.DefinedFor = 'Both';
        StationId.TranslationKey = 'unknowntag.stationid';
        StationId.Editable = false;
        StationId.Filterable = true;
        StationId.Sortable = true;
        StationId.CssClasses = '';
        StationId.value = (unknowntag) => unknowntag.StationId;
        StationId.setValue = () => {};
        StationId.DetailEditor = markRaw(InputSelect);
        StationId.DetailEditorProps = { datasource: { mapsource: StationsSource }, readonly: true };
        StationId.CellEditor = markRaw(InputSelect);
        StationId.CellEditorProps = { datasource: { mapsource: StationsSource }, readonly: true };
        // @ts-expect-error @ts-ignore
        StationId.CellRenderer = markRaw(DataSourceLookup);
        StationId.CellRendererProps = { datasource: { mapsource: StationsSource }, readonly: true };
        StationId.ExportValueFormatter = (obj: UnknownTag) => {
            return StationsSource.value.get(obj.StationId)?.text;
        };
        StationId.Validator = null;
        StationId.SortDirection = undefined;
        StationId.cellClassRules = undefined;
        StationId.MinWidth = undefined;
        StationId.MaxWidth = undefined;
        StationId.Tooltip = null;
        this.Fields.push(StationId);

        const Time = new FieldDef<UnknownTag>();
        Time.Name = 'Time';
        Time.FieldName = 'Time';
        Time.DefinedFor = 'Both';
        Time.TranslationKey = 'unknowntag.time';
        Time.Editable = false;
        Time.Filterable = true;
        Time.Sortable = true;
        Time.CssClasses = '';
        Time.value = (unknowntag) => unknowntag.Time;
        Time.setValue = () => {};
        Time.DetailEditor = markRaw(InputDateTime);
        Time.DetailEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Time.CellEditor = markRaw(InputDateTime);
        Time.CellEditorProps = { format: 'DATETIME' as DateType, readonly: true };
        Time.CellRenderer = markRaw(DateComp);
        Time.CellRendererProps = { format: 'DATETIME' as DateType, readonly: true };
        Time.Validator = null;
        Time.SortDirection = undefined;
        Time.cellClassRules = undefined;
        Time.MinWidth = 180;
        Time.MaxWidth = undefined;
        Time.Tooltip = null;
        this.Fields.push(Time);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Stations']);
    }
}
